/**
 * 大会本部が配信する「計測するマーカー」のお知らせを読み、選んだチーム
 * （ゼッケン番号）が入っているかを照合する（docs/08 §200）。
 *
 * 一関・平泉BF（2026-10）では、ターゲットのタスクの後にオクレンジャーで
 * 次のような本文が届いた（佐賀のWhatsAppも同じ運営・同じ書式の見込み）:
 *
 *   Task 9 JDG Target(1806/0288)          ← 題名（タスク番号・種目・ターゲット座標。佐賀は「【Task_5_HWZ_Target（1846/7671）】」）
 *   Measuring team will measure following Markers.
 *   #1
 *   #2
 *   …                                      ← 計測範囲に入ったマーカー（ゼッケン番号）
 *   10/10 09:16                            ← 配信時刻
 *
 * 1つのタスクにターゲットが複数あると（HWZ など）、ターゲットごとに別の
 * お知らせが届く（Task 2 HWZ Target(1636/0523) と (1414/0479)）。
 * どれか1つに載っていれば、そのタスクでは「入った」とみなす。
 *
 * 公式結果（eKLIPS）と照らし合わせると、載っていない番号はすべてロガーの粗い
 * 結果（226m 等）か No Result だった。載っている番号はほとんどが巻尺の細かい
 * 結果（0.32m 等）だが、粗い結果のものもあった（#10 Task 4 の 278m 等）。
 * つまり「載っている＝マーカーが計測範囲に入った」であって、良い結果とは限らない
 * （2026-10-11 実測）。
 */

import { pibalSessionOf } from './official-pibal.ts';

export interface TaskMarkerNotice {
  /** 同じお知らせを見分ける鍵（配信時刻＋題名） */
  id: string;
  title: string;
  taskNo: number;
  /** 種目（JDG・HWZ・HNH…）。読めなければ空 */
  taskType: string;
  /** ターゲットの座標（例 "1806/0288"）。書かれていなければ null */
  target: string | null;
  /** 計測するマーカー（ゼッケン番号）、小さい順 */
  markers: number[];
  /** 配信時刻（現地、YYYY-MM-DDTHH:MM） */
  sentLocal: string;
  /** 午前・午後のまとまり（YYYY-MM-DD-am / -pm） */
  session: string;
}

// 佐賀（WhatsApp）の題名は「【Task_5_HWZ_Target（1846/7671）】」。単語の区切りが下線、括弧が全角のことがある
// （docs/08 §222）。一関（オクレンジャー）の「Task 9 JDG Target(1806/0288)」と両方読む
const TITLE_RE = /task[\s_]*#?[\s_]*(\d+)[\s_]*([A-Za-z]{2,4})?[^()（）]*?target[\s_]*(?:[(（]\s*([\d\s/／,-]+?)\s*[)）])?/i;
const SENT_RE = /^(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})$/;

/** 題名が計測ターゲットのお知らせか */
export function isTaskMarkerTitle(title: string): boolean {
  return TITLE_RE.test(title);
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * お知らせの本文を読む。`lines`は本文の行（題名を含んでもよい）。
 * 題名が読めない・マーカーが1つも無ければ null
 */
export function parseTaskMarkerNotice(
  input: string | string[],
  options: {
    title?: string;
    /** 今の現地の年月（配信時刻に年を補う） */
    reference: { year: number; month: number };
    /** 本文に配信時刻が無いとき（貼り付け）に使う現地時刻 YYYY-MM-DDTHH:MM */
    fallbackSentLocal?: string;
  },
): TaskMarkerNotice | null {
  const lines = (Array.isArray(input) ? input : input.split(/\r?\n/))
    .map((l) => l.replace(/[　 ]/g, ' ').replace(/\s+/g, ' ').trim())
    .filter((l) => l !== '');
  const title = options.title ?? lines.find((l) => TITLE_RE.test(l)) ?? '';
  const t = TITLE_RE.exec(title);
  if (!t) return null;
  const markers = new Set<number>();
  let sentLocal: string | null = null;
  for (const line of lines) {
    if (line === title) continue;
    const sent = SENT_RE.exec(line);
    if (sent) {
      const month = Number(sent[1]);
      const year = month - options.reference.month > 1 ? options.reference.year - 1 : options.reference.year;
      sentLocal = `${year}-${pad2(month)}-${pad2(Number(sent[2]))}T${pad2(Number(sent[3]))}:${sent[4]}`;
      continue;
    }
    // 「#1」「#1, #2」「# 12」のどれでも拾う
    for (const m of line.matchAll(/#\s*(\d{1,4})\b/g)) markers.add(Number(m[1]));
  }
  if (markers.size === 0) return null;
  sentLocal ??= options.fallbackSentLocal ?? null;
  if (!sentLocal) return null;
  const target = t[3] ? t[3].replace(/\s+/g, '').replace(/[,／-]/, '/') : null;
  return {
    id: `${sentLocal}|${title}`,
    title,
    taskNo: Number(t[1]),
    taskType: (t[2] ?? '').toUpperCase(),
    target,
    markers: [...markers].sort((a, b) => a - b),
    sentLocal,
    session: pibalSessionOf(sentLocal),
  };
}

export type MarkerStatus = 'in' | 'out';

export interface TaskMarkerRow {
  taskNo: number;
  taskType: string;
  /** そのタスクのお知らせ（ターゲットごと）、ターゲット順 */
  notices: TaskMarkerNotice[];
  /** 選んだ番号ごとの結果（どれかのターゲットに載っていれば in） */
  status: Map<number, MarkerStatus>;
  /** 最後に届いたお知らせの配信時刻 */
  latestSentLocal: string;
}

/**
 * お知らせをタスクごとにまとめ、選んだ番号（ゼッケン）が入っているかを照合する。
 * 同じタスク・同じターゲットのお知らせが複数あれば（訂正の再配信）、新しい方を使う。
 * 新しいタスクが先頭
 */
export function matchTaskMarkers(notices: TaskMarkerNotice[], bibs: number[]): TaskMarkerRow[] {
  const latest = new Map<string, TaskMarkerNotice>();
  for (const n of notices) {
    const key = `${n.session}|${n.taskNo}|${n.target ?? ''}`;
    const prev = latest.get(key);
    if (!prev || prev.sentLocal < n.sentLocal) latest.set(key, n);
  }
  const byTask = new Map<string, TaskMarkerNotice[]>();
  for (const n of latest.values()) {
    // タスク番号は大会を通して重ならない前提。日付をまたいで同じ番号が来たら別に扱う
    const key = `${n.sentLocal.slice(0, 10)}|${n.taskNo}`;
    byTask.set(key, [...(byTask.get(key) ?? []), n]);
  }
  const rows: TaskMarkerRow[] = [...byTask.values()].map((list) => {
    list.sort((a, b) => (a.target ?? '').localeCompare(b.target ?? ''));
    const status = new Map<number, MarkerStatus>();
    for (const bib of bibs) status.set(bib, list.some((n) => n.markers.includes(bib)) ? 'in' : 'out');
    return {
      taskNo: list[0].taskNo,
      taskType: list.find((n) => n.taskType)?.taskType ?? '',
      notices: list,
      status,
      latestSentLocal: list.reduce((a, n) => (n.sentLocal > a ? n.sentLocal : a), ''),
    };
  });
  return rows.sort((a, b) => b.latestSentLocal.localeCompare(a.latestSentLocal) || b.taskNo - a.taskNo);
}
